import { z } from 'zod';
import { kidPinSetSchema, screenNameSchema } from './auth.js';
import { buddyApprovalModeSchema } from './buddies.js';

/** A parent creating/updating a kid profile inside their family. */
export const createKidSchema = z.object({
  screenName: screenNameSchema,
  /** Optional: parent may set an initial PIN; otherwise one is generated. */
  pin: kidPinSetSchema.optional(),
  /** Optional birth year for age-appropriate defaults (never a full DOB). */
  birthYear: z
    .number()
    .int()
    .min(2005)
    .max(new Date().getFullYear())
    .optional(),
});
export type CreateKidInput = z.infer<typeof createKidSchema>;

/** A child's daily buddy-chat (DM) budget in minutes; unset/null = unlimited. */
export const imDailyMinutesSchema = z.number().int().min(5).max(480).nullable();

/** Minutes since family-local midnight (0 = 00:00, 1439 = 23:59). */
export const imWindowMinuteSchema = z.number().int().min(0).max(1439).nullable();

export const updateKidSchema = z
  .object({
    screenName: screenNameSchema.optional(),
    active: z.boolean().optional(),
    /** Buddy chat (1:1 DM) gate for this child. Off by default (safest). */
    imEnabled: z.boolean().optional(),
    imDailyMinutes: imDailyMinutesSchema.optional(),
    /** Allowed-hours window (B3 phase 2) — always set/cleared as a pair. */
    imStartMinute: imWindowMinuteSchema.optional(),
    imEndMinute: imWindowMinuteSchema.optional(),
  })
  .refine(
    (v) => {
      const { imStartMinute: s, imEndMinute: e } = v;
      if (s === undefined && e === undefined) return true; // neither touched
      if (s === undefined || e === undefined) return false; // must set both together
      if (s === null && e === null) return true; // clearing the window
      if (s === null || e === null) return false; // can't clear only one side
      return s < e; // same-day window only
    },
    { message: 'invalid_im_window', path: ['imEndMinute'] },
  );
export type UpdateKidInput = z.infer<typeof updateKidSchema>;

export const resetKidPinSchema = z.object({
  pin: kidPinSetSchema.optional(),
});
export type ResetKidPinInput = z.infer<typeof resetKidPinSchema>;

/** Family-level safety defaults that seed each new room's policy. */
export const familySettingsSchema = z.object({
  allowLinks: z.boolean(),
  allowImages: z.boolean(),
  allowEmoji: z.boolean(),
  /** How this family handles incoming buddy requests. Optional for back-compat. */
  buddyApprovalMode: buddyApprovalModeSchema.optional(),
  /** IANA zone, e.g. "America/New_York". Optional for back-compat. */
  timezone: z
    .string()
    .refine(isValidTimeZone, { message: 'invalid_timezone' })
    .optional(),
});
export type FamilySettingsInput = z.infer<typeof familySettingsSchema>;

/** Validates an IANA timezone name using the runtime's own tz database. */
export function isValidTimeZone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
